/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: FaxDeviceFormView.tsx
 🗓️ Created: 2025-12-03
 🧩 Description:
    Form view for creating/editing fax devices
────────────────────────────────────────────────────────────────*/

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Printer, Settings, Search, Plus, Copy } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FormBox } from '../../components/ui/FormBox';
import Select from '../../components/ui/select';
import Toggle from '../../components/ui/Toggle';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { fetchFaxDeviceById, fetchFaxDevices, createFaxDevice, updateFaxDevice, deleteFaxDevice } from './api/faxDeviceApi';
import type { FaxDeviceFormState, FaxDevice, FaxDeviceType, FaxDirection } from './types';
import { FaxDeviceTechnicalSettings } from './components/FaxDeviceTechnicalSettings';

const defaultFormData: FaxDeviceFormState = {
  name: '',
  number: '',
  type: 'VIRTUAL',

  direction: 'both',
  emailTo: '',
  emailFrom: '',
  storagePath: '/var/lib/ring2all/fax/',
  t38Enabled: true,
  ecmEnabled: true,
  retries: 3,
  retryInterval: 300,
  resolution: '204x196',
  paperSize: 'A4',
  convertToPdf: true,
  autoDeleteAfterDays: 0,
  notifyOnReceive: true,
  notifyOnSend: true,
  notifyOnFail: true,
  settings: {},
  enabled: true,
  // Advanced settings
  faxIdent: '',
  faxHeader: '',
};

export default function FaxDeviceFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();

  const isEditMode = !!id && id !== 'new';
  const mode = isEditMode ? 'edit' : 'create';
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<FaxDeviceFormState>(defaultFormData);
  const [originalData, setOriginalData] = useState<FaxDeviceFormState>(defaultFormData);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Quick list state
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListDevices, setQuickListDevices] = useState<FaxDevice[]>([]);
  const [initialDevices, setInitialDevices] = useState<FaxDevice[]>([]);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const quickListLoadingRef = useRef(false);

  // Load fax device data in edit mode or duplicate mode
  useEffect(() => {
    const loadId = isEditMode ? id : duplicateId;

    if (loadId) {
      setIsLoading(true);
      fetchFaxDeviceById(loadId)
        .then((device: FaxDevice) => {
          const loadedData: FaxDeviceFormState = {
            name: duplicateId ? `${device.name} (Copy)` : device.name,
            number: duplicateId ? '' : device.number, // Clear number on duplicate
            type: device.type,

            direction: device.direction,
            emailTo: device.emailTo || '',
            emailFrom: device.emailFrom || '',
            storagePath: device.storagePath,
            t38Enabled: device.t38Enabled,
            ecmEnabled: device.ecmEnabled,
            retries: device.retries,
            retryInterval: device.retryInterval,
            resolution: device.resolution,
            paperSize: device.paperSize,
            convertToPdf: device.convertToPdf,
            autoDeleteAfterDays: device.autoDeleteAfterDays,
            notifyOnReceive: device.notifyOnReceive,
            notifyOnSend: device.notifyOnSend,
            notifyOnFail: device.notifyOnFail,
            settings: device.settings || {},
            enabled: device.enabled,
            // Advanced settings stored in JSONB
            faxIdent: (device.settings as Record<string, unknown>)?.['fax_ident'] as string || '',
            faxHeader: (device.settings as Record<string, unknown>)?.['fax_header'] as string || '',
          };
          setFormData(loadedData);
          if (isEditMode) {
            setOriginalData(loadedData);
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          showError(error.message || t('faxDevices:messages.loadError'));
          navigate('/pbx/virtual-faxes/devices');
        })
        .finally(() => setIsLoading(false));
    } else if (!isEditMode && !duplicateId) {
      // Reset to default when in new mode without duplicate
      setFormData({ ...defaultFormData });
      setOriginalData({ ...defaultFormData });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, duplicateId, isEditMode, location.key]);

  // Check for changes
  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  // Handle form field changes
  const handleChange = useCallback(<K extends keyof FaxDeviceFormState>(field: K, value: FaxDeviceFormState[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handle cancel - Reset form to original state without confirmation
  const handleCancel = useCallback(() => {
    setFormData({ ...originalData });
  }, [originalData]);

  // Handle back - Navigate to list
  const handleGoToList = useCallback(() => {
    if (hasChanges) {
      setShowCancelModal(true);
      return;
    }
    navigate('/pbx/virtual-faxes/devices');
  }, [hasChanges, navigate]);

  const confirmCancel = useCallback(() => {
    setShowCancelModal(false);
    navigate('/pbx/virtual-faxes/devices');
  }, [navigate]);

  // Handle save
  const handleSaveInternal = useCallback(async () => {
    // Validation
    if (!formData.name.trim()) {
      showError(t('faxDevices:validation.nameRequired'));
      return undefined;
    }
    if (!formData.number.trim() && formData.type !== 'USER') {
      showError(t('faxDevices:validation.numberRequired'));
      return undefined;
    }
    if (!formData.emailTo.trim() && formData.type !== 'USER') {
      showError(t('faxDevices:validation.emailRequired'));
      return undefined;
    }

    setIsSaving(true);
    try {
      let savedData;
      // Merge faxIdent / faxHeader into settings JSONB before saving
      const payloadData: FaxDeviceFormState = {
        ...formData,
        settings: {
          ...formData.settings,
          ...(formData.faxIdent ? { fax_ident: formData.faxIdent } : {}),
          ...(formData.faxHeader ? { fax_header: formData.faxHeader } : {}),
        },
      };
      if (isEditMode) {
        await updateFaxDevice(id!, payloadData);
        savedData = { id: id, ...payloadData };
        showSuccess(t('faxDevices:messages.updateSuccess'));
      } else {
        const created = await createFaxDevice(payloadData);
        savedData = created;
        showSuccess(t('faxDevices:messages.createSuccess'));
      }

      // Update original data after successful save
      setOriginalData({ ...formData });

      return savedData;
    } catch (error) {
      showError(error instanceof Error ? error.message : t('faxDevices:messages.saveError'));
      return undefined;
    } finally {
      setIsSaving(false);
    }
  }, [formData, isEditMode, id, showSuccess, showError, t]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/virtual-faxes/devices',
    newPath: '/pbx/virtual-faxes/devices/new',
    mode,
    getEditPath: (savedData) => {
      return `/pbx/virtual-faxes/devices/${savedData.uuid ?? savedData.id}`;
    },
    getFormData: () => formData,
    storageKey: 'faxDevices:saveAndNewData',
  });

  // Delete handlers
  const handleDeleteClick = useCallback(() => {
    if (!isEditMode || !id) return;
    setShowDeleteModal(true);
  }, [isEditMode, id]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !id) return;

    setIsDeleting(true);
    try {
      await deleteFaxDevice(id);
      showSuccess(t('faxDevices:messages.deleteSuccess'));
      navigate('/pbx/virtual-faxes/devices');
    } catch (error) {
      showError(error instanceof Error ? error.message : t('faxDevices:messages.deleteError'));
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [isEditMode, id, navigate, showSuccess, showError, t]);

  // Quick list handlers
  const loadInitialDevices = useCallback(async () => {
    if (quickListLoadingRef.current) return;

    quickListLoadingRef.current = true;
    setIsLoadingInitial(true);
    try {
      const result = await fetchFaxDevices({
        page: 1,
        limit: 100,
        sortBy: 'name',
        sortOrder: 'asc',
      });
      setInitialDevices(result.data);
      setQuickListDevices(result.data);
    } catch (error) {
      setInitialDevices([]);
      setQuickListDevices([]);
    } finally {
      setIsLoadingInitial(false);
      quickListLoadingRef.current = false;
    }
  }, []);

  const loadQuickListDevices = useCallback(async (search: string) => {
    const normalizedSearch = search.trim();
    if (normalizedSearch && normalizedSearch.length < 2) {
      setQuickListDevices(initialDevices);
      return;
    }

    setIsSearching(true);
    try {
      const result = await fetchFaxDevices({
        page: 1,
        limit: 50,
        sortBy: 'name',
        sortOrder: 'asc',
      }, normalizedSearch);
      setQuickListDevices(result.data);
    } catch (error) {
      setQuickListDevices([]);
    } finally {
      setIsSearching(false);
    }
  }, [initialDevices]);

  // Load initial devices when modal opens
  useEffect(() => {
    if (showQuickList && initialDevices.length === 0 && !quickListLoadingRef.current) {
      void loadInitialDevices();
    }
  }, [showQuickList, initialDevices.length, loadInitialDevices]);

  // Debounced search for quick list
  useEffect(() => {
    if (!showQuickList) {
      setQuickListSearch('');
      return;
    }

    const normalizedSearch = quickListSearch.trim();
    if (!normalizedSearch || normalizedSearch.length < 2) {
      setQuickListDevices(initialDevices);
      return;
    }

    const timer = setTimeout(() => {
      loadQuickListDevices(quickListSearch);
    }, 800);

    return () => clearTimeout(timer);
  }, [quickListSearch, showQuickList, loadQuickListDevices, initialDevices]);

  // Navigate to new device
  const handleAddNew = useCallback(() => {
    navigate('/pbx/virtual-faxes/devices/new');
  }, [navigate]);

  // Duplicate device
  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigate(`/pbx/virtual-faxes/devices/new?duplicate=${id}`);
  }, [isEditMode, id, navigate]);

  // Device type options
  const deviceTypeOptions = useMemo(() => [
    { value: 'VIRTUAL', label: t('faxDevices:types.VIRTUAL') || 'Virtual Fax (Software)' },
    { value: 'ATA', label: t('faxDevices:types.ATA') || 'ATA Device' },
    { value: 'TRUNK', label: t('faxDevices:types.TRUNK') || 'SIP Trunk' },
    { value: 'USER', label: t('faxDevices:types.USER') || 'User Fax Extension' },
    { value: 'INBOUND_ROUTE', label: t('faxDevices:types.INBOUND_ROUTE') || 'Inbound Route Fax' }
  ], [t]);

  // Direction options
  const directionOptions = useMemo(() => [
    { value: 'inbound', label: t('faxDevices:directions.inbound') || 'Inbound Only' },
    { value: 'outbound', label: t('faxDevices:directions.outbound') || 'Outbound Only' },
    { value: 'both', label: t('faxDevices:directions.both') || 'Both (Bidirectional)' }
  ], [t]);

  // Resolution options
  const resolutionOptions = useMemo(() => [
    { value: '204x98', label: t('faxDevices:resolutions.standard') || 'Standard (204×98 dpi)' },
    { value: '204x196', label: t('faxDevices:resolutions.fine') || 'Fine (204×196 dpi)' },
    { value: '204x392', label: t('faxDevices:resolutions.superfine') || 'SuperFine (204×392 dpi)' }
  ], [t]);

  // Paper size options
  const paperSizeOptions = useMemo(() => [
    { value: 'A4', label: 'A4' },
    { value: 'Letter', label: 'Letter' },
    { value: 'Legal', label: 'Legal' }
  ], []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-20">
      {/* Header */}
      <ModuleFormHeader
        title={isEditMode ? `${t('faxDevices:editTitle')} - ${formData.name || ''}` : t('faxDevices:createTitle')}
        icon={<Printer className="h-5 w-5" />}
        backButtonText={t('faxDevices:actions.list')}
        onBack={handleGoToList}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('faxDevices:actions.quickList'),
            onClick: () => setShowQuickList(true),
          },
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('faxDevices:actions.duplicate'),
              onClick: handleDuplicateFromForm
            }]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('faxDevices:actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      {/* Form Content */}
      <div className="mt-8 space-y-4">
        {/* Basic Information */}
        <FormBox
          title={t('faxDevices:sections.basicInfo')}
          icon={<Printer className="h-5 w-5" />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
            {/* Row 1: Name | Input | Number | Input */}
            <SimpleTooltip
              content={t('faxDevices:hints.name')}
              title={t('faxDevices:fields.name')}
              position="top"
              maxWidth="400px"
            >
              <Label htmlFor="name" required className="text-left">
                {t('faxDevices:fields.name')}
              </Label>
            </SimpleTooltip>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('faxDevices:placeholders.name')}
            />
            <SimpleTooltip
              content={
                formData.type === 'USER'
                  ? t('faxDevices:hints.numberUser', 'Optional for User type. If left empty, the associated extension number will be used for routing.')
                  : t('faxDevices:hints.number')
              }
              title={t('faxDevices:fields.number')}
              position="top"
              maxWidth="400px"
            >
              <Label htmlFor="number" required={formData.type !== 'USER'} className="text-left">
                {t('faxDevices:fields.number')}
              </Label>
            </SimpleTooltip>
            <Input
              id="number"
              value={formData.number}
              onChange={(e) => handleChange('number', e.target.value)}
              placeholder={
                formData.type === 'USER'
                  ? t('faxDevices:placeholders.numberUser', 'Optional – uses extension number')
                  : t('faxDevices:placeholders.number')
              }
            />

            {/* Row 2: Type | Select | Direction | Select */}
            <SimpleTooltip
              content={t('faxDevices:hints.type')}
              title={t('faxDevices:fields.type')}
              position="top"
              maxWidth="400px"
            >
              <Label htmlFor="type" required className="text-left">
                {t('faxDevices:fields.type')}
              </Label>
            </SimpleTooltip>
            <Select
              value={formData.type}
              onValueChange={(value) => handleChange('type', value as FaxDeviceType)}
              options={deviceTypeOptions}
            />
            <SimpleTooltip
              content={t('faxDevices:hints.direction')}
              title={t('faxDevices:fields.direction')}
              position="top"
              maxWidth="400px"
            >
              <Label htmlFor="direction" required className="text-left">
                {t('faxDevices:fields.direction')}
              </Label>
            </SimpleTooltip>
            <Select
              value={formData.direction}
              onValueChange={(value) => handleChange('direction', value as FaxDirection)}
              options={directionOptions}
            />

            {/* Row 3: Email To | Input | Email From | Input */}
            <SimpleTooltip
              content={
                formData.type === 'USER'
                  ? t('faxDevices:hints.emailToUser', 'Optional for User type. If left empty, the associated extension email address will be used for fax delivery.')
                  : t('faxDevices:hints.emailTo')
              }
              title={t('faxDevices:fields.emailTo')}
              position="top"
              maxWidth="400px"
            >
              <Label htmlFor="emailTo" required={formData.type !== 'USER'} className="text-left">
                {t('faxDevices:fields.emailTo')}
              </Label>
            </SimpleTooltip>
            <Input
              id="emailTo"
              type="email"
              value={formData.emailTo}
              onChange={(e) => handleChange('emailTo', e.target.value)}
              placeholder={
                formData.type === 'USER'
                  ? t('faxDevices:placeholders.emailToUser', 'Optional – uses extension email')
                  : t('faxDevices:placeholders.emailTo')
              }
            />
            <SimpleTooltip
              content={t('faxDevices:hints.emailFrom')}
              title={t('faxDevices:fields.emailFrom')}
              position="top"
              maxWidth="400px"
            >
              <Label htmlFor="emailFrom" className="text-left">
                {t('faxDevices:fields.emailFrom')}
              </Label>
            </SimpleTooltip>
            <Input
              id="emailFrom"
              type="email"
              value={formData.emailFrom}
              onChange={(e) => handleChange('emailFrom', e.target.value)}
              placeholder={t('faxDevices:placeholders.emailFrom')}
            />

            {/* Row 4: Retries | Input | Retry Interval | Input */}
            <SimpleTooltip
              content={t('faxDevices:hints.retries')}
              title={t('faxDevices:fields.retries')}
              position="top"
              maxWidth="400px"
            >
              <Label htmlFor="retries" className="text-left">
                {t('faxDevices:fields.retries')}
              </Label>
            </SimpleTooltip>
            <Input
              id="retries"
              type="number"
              min="0"
              max="10"
              value={formData.retries}
              onChange={(e) => handleChange('retries', parseInt(e.target.value) || 0)}
            />
            <SimpleTooltip
              content={t('faxDevices:hints.retryInterval')}
              title={t('faxDevices:fields.retryInterval')}
              position="top"
              maxWidth="400px"
            >
              <Label htmlFor="retryInterval" className="text-left">
                {t('faxDevices:fields.retryInterval')}
              </Label>
            </SimpleTooltip>
            <Input
              id="retryInterval"
              type="number"
              min="0"
              value={formData.retryInterval}
              onChange={(e) => handleChange('retryInterval', parseInt(e.target.value) || 0)}
            />

            {/* Row 5: Notify On Receive | Toggle | Notify On Send | Toggle */}
            <SimpleTooltip
              content={t('faxDevices:hints.notifyOnReceive')}
              title={t('faxDevices:fields.notifyOnReceive')}
              position="top"
              maxWidth="400px"
            >
              <Label htmlFor="notifyOnReceive" className="text-left">
                {t('faxDevices:fields.notifyOnReceive')}
              </Label>
            </SimpleTooltip>
            <Toggle
              id="notifyOnReceive"
              value={formData.notifyOnReceive}
              onChange={(value) => handleChange('notifyOnReceive', value)}
            />
            <SimpleTooltip
              content={t('faxDevices:hints.notifyOnSend')}
              title={t('faxDevices:fields.notifyOnSend')}
              position="top"
              maxWidth="400px"
            >
              <Label htmlFor="notifyOnSend" className="text-left">
                {t('faxDevices:fields.notifyOnSend')}
              </Label>
            </SimpleTooltip>
            <Toggle
              id="notifyOnSend"
              value={formData.notifyOnSend}
              onChange={(value) => handleChange('notifyOnSend', value)}
            />

            {/* Row 6: Notify On Fail | Toggle | Enabled | Toggle */}
            <SimpleTooltip
              content={t('faxDevices:hints.notifyOnFail')}
              title={t('faxDevices:fields.notifyOnFail')}
              position="top"
              maxWidth="400px"
            >
              <Label htmlFor="notifyOnFail" className="text-left">
                {t('faxDevices:fields.notifyOnFail')}
              </Label>
            </SimpleTooltip>
            <Toggle
              id="notifyOnFail"
              value={formData.notifyOnFail}
              onChange={(value) => handleChange('notifyOnFail', value)}
            />
            <SimpleTooltip
              content={t('faxDevices:hints.enabled')}
              title={t('faxDevices:fields.enabled')}
              position="top"
              maxWidth="400px"
            >
              <Label htmlFor="enabled" className="text-left">
                {t('faxDevices:fields.enabled')}
              </Label>
            </SimpleTooltip>
            <Toggle
              id="enabled"
              value={formData.enabled}
              onChange={(value) => handleChange('enabled', value)}
            />


          </div>
        </FormBox>

        {/* Technical Settings */}
        <FaxDeviceTechnicalSettings
          formData={formData}
          onFieldChange={handleChange}
          t={t}
          resolutionOptions={resolutionOptions}
          paperSizeOptions={paperSizeOptions}
        />
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={handleDeleteClick}
        isEditMode={isEditMode}
        isSaving={isSaving}
        cancelText={t('common:cancel')}
        deleteText={t('faxDevices:actions.delete')}
        useSaveDropdown={true}
        saveDropdownStorageKey="faxDevices:saveAction"
        saveDisabled={isSaving}
      />

      <ConfirmationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={confirmCancel}
        title={t('common.confirmCancel', 'Discard Changes?')}
        message={t('common:unsavedChanges', 'You have unsaved changes. Are you sure you want to discard them?')}
        confirmText={t('common.discard', 'Discard')}
        cancelText={t('common.keepEditing', 'Keep Editing')}
        type="warning"
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('faxDevices:messages.deleteTitle')}
        message={t('faxDevices:messages.confirmDelete', { name: formData.name })}
        confirmText={t('faxDevices:actions.delete')}
        cancelText={t('faxDevices:actions.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('faxDevices:actions.quickList')}
        items={quickListDevices}
        isLoadingInitial={isLoadingInitial}
        isSearching={isSearching}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(device) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/virtual-faxes/devices/${device.uuid ?? device.id}`);
        }}
        currentItemId={id ? parseInt(id) : undefined}
        renderItem={(device, isCurrent) => (
          <button
            onClick={() => {
              if (!isCurrent) {
                setShowQuickList(false);
                setQuickListSearch('');
                navigate(`/pbx/virtual-faxes/devices/${device.uuid ?? device.id}`);
              }
            }}
            disabled={isCurrent}
            className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent
                ? 'bg-primary/20 border-2 border-primary cursor-default'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'
              }`}
          >
            <div className="flex items-center gap-3">
              <Printer className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground">
                    {device.number}
                  </span>
                  {isCurrent && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">
                      {t('faxDevices:current')}
                    </span>
                  )}
                </div>
                {device.name && (
                  <div className="text-sm text-muted-foreground truncate">{device.name}</div>
                )}
              </div>
            </div>
          </button>
        )}
        searchPlaceholder={t('faxDevices:quickListSearch')}
        minCharsMessage={t('faxDevices:quickListMinChars')}
        emptyMessage={t('faxDevices:quickListPrompt')}
        noResultsMessage={t('faxDevices:quickListNoResults')}
        headerIcon={<Printer className="h-5 w-5" />}
        minSearchChars={2}
      />
    </div>
  );
}

